import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Business as WorkspaceIcon,
  Check as CheckIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useCurrentWorkspaceId } from '@/store/auth.store';
import { getErrorMessage } from '@/api/client';
import { useCreateWorkspace, useWorkspaces } from '../hooks/useWorkspaces';
import { useWorkspaceSwitch } from '../hooks/useWorkspaceSwitch';

export function WorkspaceSelector() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const currentWorkspaceId = useCurrentWorkspaceId();
  const { switchWorkspace } = useWorkspaceSwitch();
  const { data: workspaces = [], isLoading } = useWorkspaces();
  const createWorkspaceMutation = useCreateWorkspace();

  const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (workspaceId: number) => {
    switchWorkspace(workspaceId);
    handleClose();
  };

  const handleOpenCreateDialog = () => {
    handleClose();
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const resetCreateDialog = () => {
    setCreateDialogOpen(false);
    setWorkspaceName('');
    setWorkspaceDescription('');
    setCreateError(null);
  };

  const handleCloseCreateDialog = () => {
    if (createWorkspaceMutation.isPending) return;
    resetCreateDialog();
  };

  const handleCreateWorkspace = async () => {
    const name = workspaceName.trim();
    if (!name) return;

    try {
      const workspace = await createWorkspaceMutation.mutateAsync({
        name,
        ...(workspaceDescription.trim() ? { description: workspaceDescription.trim() } : {}),
      });
      switchWorkspace(workspace.id);
      resetCreateDialog();
    } catch (err) {
      setCreateError(getErrorMessage(err));
    }
  };

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  return (
    <>
      <Button
        color="inherit"
        onClick={handleClick}
        startIcon={<WorkspaceIcon />}
        endIcon={<ExpandMoreIcon />}
        sx={{ textTransform: 'none' }}
      >
        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
          {currentWorkspace?.name ?? 'Select Workspace'}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 220, maxWidth: 320 } } }}
      >
        {workspaces.map((workspace) => (
          <MenuItem
            key={workspace.id}
            selected={workspace.id === currentWorkspaceId}
            onClick={() => handleSelect(workspace.id)}
          >
            {workspace.id === currentWorkspaceId && (
              <ListItemIcon>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            )}
            <ListItemText inset={workspace.id !== currentWorkspaceId}>
              {workspace.name}
            </ListItemText>
          </MenuItem>
        ))}
        {workspaces.length > 0 && <Divider />}
        <MenuItem onClick={handleOpenCreateDialog}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Workspace</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="xs">
        <DialogTitle>Create Workspace</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Workspace name"
            value={workspaceName}
            onChange={(event) => setWorkspaceName(event.target.value)}
            disabled={createWorkspaceMutation.isPending}
            required
          />
          <TextField
            label="Description"
            value={workspaceDescription}
            onChange={(event) => setWorkspaceDescription(event.target.value)}
            disabled={createWorkspaceMutation.isPending}
            multiline
            minRows={2}
          />
          {createError && <FormHelperText error>{createError}</FormHelperText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={createWorkspaceMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateWorkspace}
            disabled={!workspaceName.trim() || createWorkspaceMutation.isPending}
          >
            {createWorkspaceMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default WorkspaceSelector;
